import type {
  GamificationRewardOutcome,
  GamificationRewardOutcomeStatus,
} from "@core/gamification";
import { publishLeagueCard } from "@app/league/atoms/league-card.atom";
import {
  isLeagueCardVisible,
  mergeLeagueCards,
} from "@app/league/utils/merge-league-cards";
import { parseLeagueCard } from "@core/league/utils/parse-league";
import { createLatinNumberFormatter } from "@core/shared/utils/locale-formatters";
import { URLS } from "@core/shared/utils/urls";
import {
  infoToast,
  successToast,
  warningToast,
} from "@design-system/components/toast/Toast";
import { getCurrentLocaleCode, plainTrans, trans } from "@mongez/localization";
import { createElement } from "react";
import { RewardOutcomeNotice } from "./components/RewardOutcomeNotice";
import {
  type RewardCurrencyKey,
  rewardCurrencyKeys,
} from "./reward-outcome-statuses";

const noticeStatusOrder: GamificationRewardOutcomeStatus[] = [
  "awarded",
  "reversed",
  "capped",
  "unavailable",
  "ineligible",
  "disabled",
  "duplicate",
];

const noticeToneByStatus = {
  awarded: "success",
  reversed: "info",
  capped: "warning",
  unavailable: "warning",
  ineligible: "info",
  disabled: "info",
  duplicate: "info",
} as const;

/**
 * Final customer-facing result for one balance after resolving chronological
 * backend acknowledgements.
 *
 * @example
 * const resolution: ResolvedGamificationCurrencyOutcome = {
 *   currency: "xp",
 *   status: "awarded",
 *   delta: 20,
 * };
 */
export type ResolvedGamificationCurrencyOutcome = {
  currency: RewardCurrencyKey;
  status: GamificationRewardOutcomeStatus;
  delta?: number;
};

function rewardActivityToastOptions() {
  return {
    action: {
      label: trans("gamification.rewardOutcomes.viewActivity"),
      onClick: () => window.location.assign(URLS.gamification),
    },
  };
}

function rewardOutcomeDirection(): "ltr" | "rtl" {
  return getCurrentLocaleCode() === "ar" ? "rtl" : "ltr";
}

function showRewardOutcomeNotice(
  status: GamificationRewardOutcomeStatus,
  description: string,
): void {
  const notice = createElement(RewardOutcomeNotice, {
    description,
    direction: rewardOutcomeDirection(),
    title: trans(`gamification.rewardOutcomes.title.${status}`),
  });
  const toastOptions = rewardActivityToastOptions();
  const tone = noticeToneByStatus[status];

  if (tone === "success") {
    successToast(notice, toastOptions);
    return;
  }

  if (tone === "warning") {
    warningToast(notice, toastOptions);
    return;
  }

  infoToast(notice, toastOptions);
}

function currencyName(currency: RewardCurrencyKey): string {
  return plainTrans(`gamification.currency.${currency}Label`);
}

function currencyLabel(
  currency: RewardCurrencyKey,
  delta: number,
  numberFormatter: ReturnType<typeof createLatinNumberFormatter>,
): string {
  const value = `${delta > 0 ? "+" : ""}${numberFormatter.format(delta)}`;

  return plainTrans(`gamification.currency.${currency}Value`, { value });
}

function resolvedDeltas(
  resolutions: ResolvedGamificationCurrencyOutcome[],
  acceptedStatus: "awarded" | "reversed",
): string[] {
  const numberFormatter = createLatinNumberFormatter(
    {},
    getCurrentLocaleCode(),
  );

  return resolutions.flatMap((resolution) => {
    if (
      resolution.status !== acceptedStatus ||
      !Number.isFinite(resolution.delta) ||
      resolution.delta === 0
    ) {
      return [];
    }

    return [
      currencyLabel(
        resolution.currency,
        Number(resolution.delta),
        numberFormatter,
      ),
    ];
  });
}

function resolveCurrencyOutcome(
  outcomes: GamificationRewardOutcome[],
  currency: RewardCurrencyKey,
): ResolvedGamificationCurrencyOutcome {
  let latestStatus = outcomes[0][currency].status;
  let latestMutationStatus: "awarded" | "reversed" | undefined;
  let mutationDelta = 0;

  for (const outcome of outcomes) {
    const currencyOutcome = outcome[currency];
    latestStatus = currencyOutcome.status;

    if (
      currencyOutcome.status !== "awarded" &&
      currencyOutcome.status !== "reversed"
    ) {
      continue;
    }

    latestMutationStatus = currencyOutcome.status;

    if (Number.isFinite(currencyOutcome.delta)) {
      mutationDelta += Number(currencyOutcome.delta);
    }
  }

  if (!latestMutationStatus) {
    return { currency, status: latestStatus };
  }

  let status: "awarded" | "reversed" = latestMutationStatus;

  if (mutationDelta > 0) {
    status = "awarded";
  } else if (mutationDelta < 0) {
    status = "reversed";
  }

  if (mutationDelta === 0) {
    return { currency, status };
  }

  return { currency, delta: mutationDelta, status };
}

/**
 * Resolve chronological backend acknowledgements to one truthful result per
 * currency.
 *
 * Confirmed balance mutations outrank no-change statuses because the backend
 * has already written those deltas. Their net delta determines whether the
 * result is awarded or reversed; a zero net uses the latest mutation. When no
 * mutation occurred, the latest producer acknowledgement wins because backend
 * response arrays preserve event chronology.
 *
 * @example
 * const resolutions = resolveGamificationRewardOutcomes(rewardOutcomes);
 */
export function resolveGamificationRewardOutcomes(
  outcomes: GamificationRewardOutcome[],
): ResolvedGamificationCurrencyOutcome[] {
  if (outcomes.length === 0) return [];

  return rewardCurrencyKeys.map((currency) =>
    resolveCurrencyOutcome(outcomes, currency),
  );
}

function rewardOutcomeDescription(
  status: GamificationRewardOutcomeStatus,
  currencies: string,
  rewards: string,
): string {
  if (status === "awarded" || status === "reversed") {
    const key = rewards
      ? `gamification.rewardOutcomes.description.${status}WithRewards`
      : `gamification.rewardOutcomes.description.${status}`;

    return trans(key, { rewards });
  }

  return trans(`gamification.rewardOutcomes.description.${status}`, {
    currencies,
  });
}

/**
 * Announce only server-confirmed reward results from one or more producers.
 *
 * @example
 * notifyGamificationRewardOutcomes(response.data.rewardOutcomes);
 */
export function notifyGamificationRewardOutcomes(
  result:
    | GamificationRewardOutcome
    | GamificationRewardOutcome[]
    | null
    | undefined,
): void {
  if (!result) return;

  const outcomes = Array.isArray(result) ? result : [result];
  const leagueCard = mergeLeagueCards(
    outcomes.map((outcome) => parseLeagueCard(outcome.leagueCard)),
  );
  const hasLeagueCard = isLeagueCardVisible(leagueCard);

  // The League card replaces the League-points line of the toast.
  if (hasLeagueCard) publishLeagueCard(leagueCard);

  const resolutions = resolveGamificationRewardOutcomes(outcomes).filter(
    (resolution) =>
      !(hasLeagueCard && resolution.currency === "leaguePoints"),
  );

  for (const status of noticeStatusOrder) {
    const statusResolutions = resolutions.filter(
      (resolution) => resolution.status === status,
    );

    if (statusResolutions.length === 0) continue;

    const rewards =
      status === "awarded" || status === "reversed"
        ? resolvedDeltas(statusResolutions, status)
        : [];
    const currencies = statusResolutions.map((resolution) =>
      currencyName(resolution.currency),
    );

    showRewardOutcomeNotice(
      status,
      rewardOutcomeDescription(
        status,
        currencies.join(plainTrans("gamification.rewardOutcomes.separator")),
        rewards.join(plainTrans("gamification.rewardOutcomes.separator")),
      ),
    );
  }
}
